/**
 * 表單預覽 iframe 工具
 * 完整 HTML 文件（DOCTYPE + head + body）用 dangerouslySetInnerHTML 注入到 div 時無法正確渲染，
 * 需以 iframe + Blob URL 載入為獨立文件才能顯示樣式與結構。
 * 與 Reports/EFormInstanceReports 預覽方式一致。
 */

/**
 * 清理 GrapesJS / 設計器匯出的 HTML：移除嵌套 body、meta、title 等無效 fragment 標籤，
 * 保留 style 區塊，使內容能正確以 innerHTML 或 iframe 渲染。
 * @param {string} html
 * @returns {string}
 */
export function normalizeEformHtmlFragment(html) {
  if (!html || typeof html !== 'string') return '';
  let result = html.trim();
  if (!result) return '';

  const extractStyles = (source) => {
    const styles = [];
    const withoutStyles = source.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, (match) => {
      styles.push(match);
      return '';
    });
    return { styles: styles.join('\n'), rest: withoutStyles };
  };

  const stripBodyWrappers = (content) => content
    .replace(/<\/?body[^>]*>/gi, '')
    .replace(/<meta[^>]*\/?>/gi, '')
    .replace(/<title[^>]*>[\s\S]*?<\/title>/gi, '')
    .replace(/<link[^>]*\/?>/gi, '')
    .trim();

  if (/^\s*<!DOCTYPE\s/i.test(result) || /^\s*<html[\s>]/i.test(result)) {
    const { styles, rest } = extractStyles(result);
    const bodyBlocks = rest.match(/<body[^>]*>[\s\S]*?<\/body>/gi);
    const bodyInner = bodyBlocks
      ? stripBodyWrappers(bodyBlocks[bodyBlocks.length - 1])
      : stripBodyWrappers(rest);
    return styles + bodyInner;
  }

  if (/<body[\s>]/i.test(result)) {
    const { styles, rest } = extractStyles(result);
    return styles + stripBodyWrappers(rest);
  }

  return result;
}

/**
 * 將相對路徑的圖片 src 轉為絕對路徑
 * @param {string} html
 * @param {string} baseUrl
 * @returns {string}
 */
export function withAbsoluteImageUrls(html, baseUrl = typeof window !== 'undefined' ? window.location.origin : '') {
  if (!html) return '';
  return html.replace(
    /<img([^>]*)\ssrc=["']([^"']+)["']([^>]*)>/gi,
    (match, before, src, after) => {
      if (src && src.startsWith('/') && !src.startsWith('//') && !src.match(/^https?:\/\//i)) {
        return `<img${before} src="${baseUrl}${src}"${after}>`;
      }
      return match;
    }
  );
}

/**
 * 建立用於 iframe 的 Blob URL，供表單預覽使用
 * 若 html 已是完整文件（<!DOCTYPE 或 <html），則只做圖片路徑處理；否則包一層最小 document。
 * @param {string} html - filledHtmlCode 或 htmlCode
 * @param {string} baseUrl
 * @returns {string} blob URL，用畢需 URL.revokeObjectURL
 */
export function buildFormPreviewBlobUrl(html, baseUrl = typeof window !== 'undefined' ? window.location.origin : '') {
  if (!html || typeof html !== 'string') return '';
  const normalized = normalizeEformHtmlFragment(html);
  const processed = withAbsoluteImageUrls(normalized, baseUrl);
  const trimmed = processed.trim();
  const isFullDocument = /^\s*<!DOCTYPE\s/i.test(trimmed) || /^\s*<html\s/i.test(trimmed);
  const fullHtml = isFullDocument
    ? processed
    : `<!DOCTYPE html>
<html lang="zh-TW">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<base href="${baseUrl}/">
<style>body{margin:0;padding:20px;font-family:Arial,sans-serif;background:#fff;}*{box-sizing:border-box;}table{width:100% !important;}img{max-width:100% !important;height:auto !important;}</style>
</head>
<body>
${processed}
</body>
</html>`;
  const blob = new Blob([fullHtml], { type: 'text/html' });
  return URL.createObjectURL(blob);
}

/**
 * 從表單 iframe 收集使用者填寫後的「完整 HTML 文件」（含 <head> 內所有 <style>/<link>/<meta>，
 * 以及 body 內 input/textarea/select/checkbox/radio 的最新值）。
 *
 * 設計重點：
 * 1) Clone 整個 documentElement 而不是只 clone body，避免 <head> 的 CSS 被丟棄。
 * 2) 在 cloned body 上同步 value/selected/checked 屬性（不直接動 live DOM），
 *    避免 modal 還開著時對使用者操作造成 side effect。
 * 3) 還原原始 <!DOCTYPE> 宣告，避免重新載入時 iframe 進入 quirks mode。
 *
 * @param {{ current: HTMLIFrameElement | null } | HTMLIFrameElement | null} iframeOrRef
 *        可傳入 React ref 物件 (`.current` 為 iframe) 或 iframe 本身。
 * @returns {string | null} 整份完整 HTML（含 DOCTYPE + html + head + body），
 *          或在 iframe 尚未載入 / 跨域 / 取不到 contentDocument 時回傳 null。
 */
export function collectFilledHtmlFromIframe(iframeOrRef) {
  try {
    const iframe = iframeOrRef && 'current' in iframeOrRef ? iframeOrRef.current : iframeOrRef;
    const doc = iframe?.contentDocument;
    if (!doc || !doc.documentElement || !doc.body) return null;

    const cloned = doc.documentElement.cloneNode(true);
    const clonedBody = cloned.querySelector('body');
    if (!clonedBody) return null;

    const origInputs = doc.body.querySelectorAll('input');
    const cloneInputs = clonedBody.querySelectorAll('input');
    origInputs.forEach((input, i) => {
      const target = cloneInputs[i];
      if (!target) return;
      const type = (input.type || '').toLowerCase();
      if (type === 'checkbox' || type === 'radio') {
        if (input.checked) target.setAttribute('checked', 'checked');
        else target.removeAttribute('checked');
      } else {
        if (input.value != null) target.setAttribute('value', input.value);
        const dataVal = input.getAttribute('data-value');
        if (dataVal != null) target.setAttribute('data-value', dataVal);
      }
    });

    const origTextareas = doc.body.querySelectorAll('textarea');
    const cloneTextareas = clonedBody.querySelectorAll('textarea');
    origTextareas.forEach((ta, i) => {
      const target = cloneTextareas[i];
      if (!target) return;
      if (ta.value != null) target.textContent = ta.value;
    });

    const origSelects = doc.body.querySelectorAll('select');
    const cloneSelects = clonedBody.querySelectorAll('select');
    origSelects.forEach((sel, i) => {
      const target = cloneSelects[i];
      if (!target) return;
      const idx = sel.selectedIndex;
      Array.from(target.options).forEach(o => o.removeAttribute('selected'));
      if (idx >= 0 && target.options[idx]) {
        target.options[idx].setAttribute('selected', 'selected');
      }
    });

    const dt = doc.doctype;
    let doctype;
    if (dt) {
      const pub = dt.publicId ? ` PUBLIC "${dt.publicId}"${dt.systemId ? ` "${dt.systemId}"` : ''}` : '';
      const sys = !dt.publicId && dt.systemId ? ` SYSTEM "${dt.systemId}"` : '';
      doctype = `<!DOCTYPE ${dt.name}${pub}${sys}>\n`;
    } else {
      doctype = '<!DOCTYPE html>\n';
    }
    return doctype + cloned.outerHTML;
  } catch (err) {
    if (typeof console !== 'undefined') {
      console.warn('[formPreviewIframeUtils] collectFilledHtmlFromIframe failed:', err);
    }
    return null;
  }
}
